import {
    Badge,
    Box,
    Button,
    Card,
    Container,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { CAMPAIGN_TYPES, OFFER_CATEGORIES } from '../../../common/enums/enums';
import {
    CustomMultiSelect,
    MultiSelectCreatable,
    SearchableSelect,
    SearchableSelectCreatable,
} from '../../../common';
import React, { useEffect, useMemo, useState } from 'react';
import {
    getMediaGroups,
    getShortlinks,
    getUserMediaGroups,
    getUserVerticals,
    getUsers,
    getVerticals,
} from '../../../../http/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import { toGroupedOptions } from '../../../../utils/enumUtils';
import useAddChatScript from '../../../../http/mutations/campaigns/useAddChatScript';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';
import useGetChatScriptByIdQuery from '../../../../http/queries/campaigns/useGetChatScriptById';
import { useTimeout } from '@mantine/hooks';

const CreateChatScriptView = ({}) => {
    const { id: scriptId } = useParams();
    const userProfile = useAuthStore((state) => state.userProfile);

    const { isLoading, data: chatScript } = useGetChatScriptByIdQuery(scriptId);

    const setInitialValues = () => ({
        id: Number.isInteger(Number.parseInt(scriptId)) ? scriptId : null,
        name: chatScript?.name || '',
        userId: hasRole(userProfile, 'SUPER_ADMIN')
            ? userProfile?.id.toString()
            : null,

        lines: chatScript?.lines.join('\n'),
        keywords: toPipeString(chatScript?.keywords),
        intros: chatScript?.intros.join('\n'),
        keepAlives: chatScript?.keepAlives.join('\n'),
        images: chatScript?.images.join('\n'),

        //
        trackingId: chatScript?.trackingId || '',
        tags: chatScript?.tags || [],
    });

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? userProfile?.id.toString()
                : null,
            keywords: toPipeString({
                key1: ['array item 1', 'array item 2', 'extra'],
                key2: ['foo', 'bar', 'baz'],
            }),
        },
        onSubmitPreventDefault: 'always',

        transformValues: (values) => {
            const splitLines = (value) => {
                if (!value) {
                    return [];
                }

                const lines = value
                    .split('\n')
                    .map((x) => x.trim())
                    .filter((x) => x?.length);

                return lines;
            };
            return {
                ...values,
                lines: splitLines(values.lines),
                keywords: toDictionary(splitLines(values.keywords)),
                intros: splitLines(values.intros),
                images: splitLines(values.images),
                keepAlives: splitLines(values.keepAlives),
            };
        },

        validate: {
            name: (value) => {
                const { type, message } = genericValidator(
                    'Script name',
                    value,
                    {
                        required: true,
                        minLength: 6,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Script name must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            trackingId: (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },
            tags: (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        if (chatScript && form.values.id != chatScript.id) {
            form.setInitialValues(setInitialValues());
            form.reset();
        } else {
            console.log('hmm');
        }
    }, [chatScript?.id]);

    const { isPending, isSuccess, mutate: addOrUpdate } = useAddChatScript();

    const navigate = useNavigate();
    const { start } = useTimeout(() => navigate('/campaigns/scripts'), 2000);

    const onSubmit = (model) => {
        if (model.type !== 'Sms') model.smsOptions = null;
        if (model.type !== 'Rvm') model.rvmOptions = null;
        if (model.type !== 'Email') model.emailOptions = null;

        if (!model.scheduleEnabled) {
            model.scheduleOptions = null;
        }

        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                start();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    if (isLoading) {
        return (
            <Container fluid>
                <Loader visible={isLoading} />
            </Container>
        );
    }

    return (
        <Container fluid>
            <Card>
                <Text
                    fw={500}
                    size='lg'
                    mt='md'>
                    {chatScript ? 'Update Script' : 'Create Script'}
                </Text>
                <Card.Section p={'lg'}>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Script Name'}
                                placeholder='Enter a name'
                                withAsterisk
                                key={form.key('name')}
                                {...form.getInputProps('name')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'User'}
                                    withAsterisk
                                    placeholder='User'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    defaultValue={
                                        chatScript?.owner || {
                                            label: userProfile.username,
                                            value: userProfile.id,
                                        }
                                    }
                                    key={form.key('userId')}
                                    {...form.getInputProps('userId')}
                                />
                            ) : null}

                            <Textarea
                                placeholder='Script Lines'
                                label='Script Lines'
                                autosize
                                minRows={4}
                                maxRows={10}
                                withAsterisk
                                key={form.key('lines')}
                                {...form.getInputProps('lines')}
                            />

                            <Textarea
                                placeholder='keyword|response 1|response 2'
                                label='Keyword Responses'
                                autosize
                                minRows={2}
                                maxRows={10}
                                key={form.key('keywords')}
                                {...form.getInputProps('keywords')}
                            />

                            <Textarea
                                placeholder='Responses to messages containing images'
                                label='Image Responses'
                                autosize
                                minRows={2}
                                maxRows={10}
                                key={form.key('images')}
                                {...form.getInputProps('images')}
                            />

                            <Textarea
                                placeholder={
                                    'Responses for incoming messages that were not solicited.'
                                }
                                label='Intro Messages'
                                autosize
                                minRows={2}
                                maxRows={10}
                                key={form.key('intros')}
                                {...form.getInputProps('intros')}
                            />

                            <Textarea
                                placeholder='Messages sent after a period of inactivity'
                                label='Keep-Alive Responses'
                                autosize
                                minRows={2}
                                maxRows={10}
                                key={form.key('keepAlives')}
                                {...form.getInputProps('keepAlives')}
                            />

                            <SearchableSelect
                                label={'Media Group'}
                                placeholder='Select media group'
                                queryHttpFn={(queryParams) =>
                                    getUserMediaGroups({
                                        userId: form.values.userId,
                                        pagination: queryParams,
                                    })
                                }
                                queryKey={
                                    form.values.userId
                                        ? [
                                              'media_groups',
                                              'sms',
                                              form.values.userId,
                                          ]
                                        : ['media_groups', 'sms']
                                }
                                optionsMapFn={(x) => ({
                                    label: x.name,
                                    value: x.id?.toString(),
                                })}
                                key={form.key('mediaGroupId')}
                                {...form.getInputProps('mediaGroupId')}
                            />

                            <TextInput
                                label={'Tracking Id'}
                                placeholder={'Custom tracking id'}
                                key={form.key('trackingId')}
                                {...form.getInputProps('trackingId')}
                            />

                            <TagsInput
                                label={'Tags'}
                                placeholder={'Tags'}
                                key={form.key('tags')}
                                {...form.getInputProps('tags')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={
                                        isPending ||
                                        isSuccess ||
                                        !form.isValid()
                                    }
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    Create
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Card.Section>
            </Card>
        </Container>
    );
};

/**
 * Converts an array of pipe-delimited lines into an object whose
 * keys are the first segment and whose values are arrays of the
 * remaining segments.
 *
 * @param {string[]} lines - e.g. ["key1|value 1|value 2", ...]
 * @param {string} [delim="|"] - delimiter to split on (optional)
 * @returns {Record<string, string[]>}
 */
const toDictionary = (lines, delim = '|') =>
    lines.reduce((acc, line) => {
        if (typeof line !== 'string') return acc; // skip non-strings
        const parts = line.split(delim).map((s) => s.trim()); // trim whitespace
        if (parts.length < 2 || !parts[0]) return acc; // need at least key+1
        const [key, ...values] = parts;

        // If a key repeats, concatenate additional values
        acc[key] = acc[key] ? acc[key].concat(values) : values;
        return acc;
    }, {});

/**
 * Converts a dictionary of { key: [values…] } back to the original
 * pipe-delimited string format produced earlier.
 *
 * @param {Record<string, string[]>} dict – e.g. { key1: ['v1', 'v2'] }
 * @param {string} [delim="|"]   – delimiter to join segments
 * @param {string} [lineSep="\n"] – line separator for multiple keys
 * @returns {string}             – e.g. "key1|v1|v2\nkey2|foo|bar"
 */
const toPipeString = (dict, delim = '|', lineSep = '\n') => {
    if (!dict) {
        return '';
    }

    return Object.entries(dict)
        .map(([key, values]) => [key, ...values].join(delim))
        .join(lineSep);
};

export default CreateChatScriptView;
